/**
 * MiniQiblaCompass — Compact Qibla compass for mobile homepage
 * 
 * Display:
 *   - Mini compass (120px SVG)
 *   - Qibla bearing (degrees)
 *   - Distance to Mecca
 *   - Alignment status (colored indicator)
 * 
 * Requirements:
 *   - coords: { lat, lng } from geolocationService
 *   - heading: device orientation (optional, for actual compass)
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Compass, AlertCircle } from 'lucide-react';
import { GlassCard } from '../shared';
import { lowPassFilterHeading } from '../../utils/sensorFiltering';
import { NativeCompass, isNativeCompassPlatform } from '../../plugins/nativeCompass';

const KAABA = { lat: 21.4225, lng: 39.8262 };

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

function bearingTo(lat1, lng1, lat2, lng2) {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lng2 - lng1);
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function alignmentTier(delta) {
  if (delta == null) return { tier: 'unknown', label: 'Non calibré' };
  const abs = Math.abs(delta);
  if (abs < 5)  return { tier: 'on',   label: '✓ Aligné', color: 'text-emerald-400' };
  if (abs < 20) return { tier: 'near', label: 'À proximité', color: 'text-amber-400' };
  return        { tier: 'far',  label: 'À recalibrer', color: 'text-rose-400' };
}

/**
 * Mini compass SVG dial (120px)
 */
const MiniCompassDial = ({ heading, qiblaBearing, hasCompass }) => {
  const size = 120;
  const r = size / 2;
  // Disc rotates so North stays fixed relative to the real world
  const discRotation = hasCompass && heading != null ? -heading : 0;
  // Qibla marker angle within disc: since disc already applies -heading,
  // absolute screen angle = -heading + qiblaBearing = correct Qibla direction
  const qiblaOnDisc = qiblaBearing != null ? qiblaBearing : 0;

  // Needle tip position (in disc-local space, before disc rotation)
  const needleLen = r - 14;
  const needleRad = toRad(qiblaOnDisc - 90); // -90 because SVG 0° = right
  const tipX = r + Math.cos(needleRad) * needleLen;
  const tipY = r + Math.sin(needleRad) * needleLen;

  // Alignment color
  const isAligned = hasCompass && heading != null && qiblaBearing != null
    && Math.abs(((qiblaBearing - heading + 540) % 360) - 180) < 5;
  const needleColor = isAligned ? '#34d399' : '#d4af37';

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="drop-shadow-lg"
      data-testid="mini-qibla-dial"
    >
      {/* Outer ring */}
      <circle cx={r} cy={r} r={r - 2} fill="rgba(255,255,255,0.05)" stroke="rgba(212,175,55,0.2)" strokeWidth="1.5" />

      {/* Rotating disc group */}
      <g
        style={{
          transform: `rotate(${discRotation}deg)`,
          transformOrigin: `${r}px ${r}px`,
          transition: 'transform 0.25s ease-out',
        }}
      >
        {/* Inner ring */}
        <circle cx={r} cy={r} r={r - 8} fill="none" stroke="rgba(212,175,55,0.08)" strokeWidth="0.5" />

        {/* Cardinal letters */}
        {[
          { dir: 'N', x: r,      y: r - 38, color: '#f87171', fw: 'bold' },
          { dir: 'E', x: r + 38, y: r,      color: '#9ca3af', fw: 'normal' },
          { dir: 'S', x: r,      y: r + 38, color: '#9ca3af', fw: 'normal' },
          { dir: 'W', x: r - 38, y: r,      color: '#9ca3af', fw: 'normal' },
        ].map(({ dir, x, y, color, fw }) => (
          <text key={dir} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
            fill={color} fontSize="9" fontWeight={fw} fontFamily="serif">
            {dir}
          </text>
        ))}

        {/* Qibla needle — points from center toward Qibla bearing on disc */}
        <line
          x1={r} y1={r}
          x2={tipX} y2={tipY}
          stroke={needleColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.9"
        />

        {/* Qibla arrowhead */}
        <polygon
          points={`${tipX},${tipY - 1} ${tipX - 4},${tipY - 8} ${tipX + 4},${tipY - 8}`}
          fill={needleColor}
          style={{
            transformOrigin: `${tipX}px ${tipY}px`,
            transform: `rotate(${qiblaOnDisc}deg)`,
          }}
        />

        {/* Kaaba icon at needle tip — emoji in SVG foreignObject */}
        <text
          x={tipX}
          y={tipY - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="14"
          style={{
            transformOrigin: `${tipX}px ${tipY}px`,
            transform: `rotate(${qiblaOnDisc}deg)`,
          }}
        >
          🕋
        </text>
      </g>

      {/* Fixed device pointer at top (north indicator for the user) */}
      <polygon
        points={`${r},4 ${r - 4},12 ${r + 4},12`}
        fill={isAligned ? '#34d399' : 'rgba(148,163,184,0.6)'}
      />

      {/* Center dot */}
      <circle cx={r} cy={r} r="3.5" fill={needleColor} />
    </svg>
  );
};

/**
 * MiniQiblaCompass Component
 * Manages its own heading state to avoid re-rendering the parent page.
 */
export const MiniQiblaCompass = ({ coords = null, loading = false, error = null }) => {
  // Internal heading state — orientation events only re-render THIS component
  const [heading, setHeading] = useState(null);
  const [hasCompass, setHasCompass] = useState(false);
  const headingRef = useRef(null);
  const nativeCompassEnabled = isNativeCompassPlatform();

  useEffect(() => {
    if (nativeCompassEnabled) {
      let listenerHandle;
      let cancelled = false;

      const handleNativeUpdate = (payload) => {
        if (cancelled) {
          return;
        }

        const rawHeading = typeof payload?.trueHeading === 'number'
          ? payload.trueHeading
          : typeof payload?.heading === 'number'
            ? payload.heading
            : payload?.magneticHeading;

        if (typeof rawHeading !== 'number' || Number.isNaN(rawHeading)) {
          return;
        }

        const smoothed = lowPassFilterHeading(headingRef.current, rawHeading, 0.85);
        headingRef.current = smoothed;
        setHeading(smoothed);
        setHasCompass(true);
      };

      (async () => {
        try {
          listenerHandle = await NativeCompass.addListener('compassUpdate', handleNativeUpdate);
          const started = await NativeCompass.start(
            coords ? { latitude: coords.lat, longitude: coords.lng } : {},
          );
          if (cancelled) {
            return;
          }
          setHasCompass(Boolean(started?.available));
          if (typeof started?.heading === 'number' || typeof started?.magneticHeading === 'number') {
            handleNativeUpdate(started);
          }
        } catch {
          setHasCompass(false);
        }
      })();

      return () => {
        cancelled = true;
        if (listenerHandle) {
          listenerHandle.remove();
        }
        NativeCompass.stop().catch(() => {});
      };
    }

    const handleOrientation = (event) => {
      let h = null;
      if (typeof event.webkitCompassHeading === 'number' && event.webkitCompassHeading >= 0) {
        // iOS: already a clockwise compass heading
        h = event.webkitCompassHeading;
      } else if (typeof event.alpha === 'number') {
        // Android: alpha is CCW → convert to CW
        h = (360 - event.alpha + 360) % 360;
      }
      if (h !== null && !Number.isNaN(h)) {
        const smoothed = lowPassFilterHeading(headingRef.current, h, 0.85);
        headingRef.current = smoothed;
        setHeading(smoothed);
      }
    };

    const setup = async () => {
      if (typeof DeviceOrientationEvent !== 'undefined'
          && typeof DeviceOrientationEvent.requestPermission === 'function') {
        try {
          const perm = await DeviceOrientationEvent.requestPermission();
          if (perm === 'granted') {
            setHasCompass(true);
            window.addEventListener('deviceorientationabsolute', handleOrientation, true);
            window.addEventListener('deviceorientation', handleOrientation, true);
          }
        } catch {
          setHasCompass(false);
        }
      } else {
        // Android / non-iOS — no permission needed
        setHasCompass(true);
        window.addEventListener('deviceorientationabsolute', handleOrientation, true);
        window.addEventListener('deviceorientation', handleOrientation, true);
      }
    };

    setup();

    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true);
      window.removeEventListener('deviceorientation', handleOrientation, true);
    };
  }, [coords, nativeCompassEnabled]); // Run once on mount
  // Calculer les valeurs avant les retours (jamais dans des conditions)
  const qiblaBearing = useMemo(
    () => (coords ? bearingTo(coords.lat, coords.lng, KAABA.lat, KAABA.lng) : null),
    [coords],
  );

  const distKm = useMemo(
    () => (coords ? distanceKm(coords.lat, coords.lng, KAABA.lat, KAABA.lng) : null),
    [coords],
  );

  const deltaToQibla = useMemo(() => {
    if (qiblaBearing == null || heading == null) return null;
    const delta = qiblaBearing - heading;
    return ((delta + 180) % 360) - 180; // Normalize to [-180, 180]
  }, [qiblaBearing, heading]);

  const tier = useMemo(
    () => alignmentTier(deltaToQibla),
    [deltaToQibla],
  );

  // Maintenant on peut faire les retours conditionnels
  if (!coords) {
    return (
      <GlassCard className="flex items-center justify-center gap-3 p-4" data-testid="mini-qibla-compass-empty">
        <Compass className="w-5 h-5 text-primary shrink-0" />
        <span className="text-sm text-text-muted">Géolocalisation requise</span>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <GlassCard className="flex items-center justify-center gap-2 p-4 bg-rose-500/5" data-testid="mini-qibla-compass-error">
        <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
        <span className="text-sm text-rose-400">{error}</span>
      </GlassCard>
    );
  }

  return (
    <GlassCard 
      className="flex gap-4 p-4 items-center"
      data-testid="mini-qibla-compass"
    >
      {/* Compass dial */}
      <div className="flex-shrink-0">
        {loading ? (
          <div className="w-[120px] h-[120px] bg-surface-elevated rounded-full animate-pulse" />
        ) : (
          <MiniCompassDial
            heading={heading}
            qiblaBearing={qiblaBearing}
            hasCompass={hasCompass}
          />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-text-muted mb-1">
          Qibla
        </div>
        
        <div className="text-2xl font-mono text-text-primary font-bold">
          {qiblaBearing?.toFixed(0)}°
        </div>
        
        {distKm != null && (
          <div className="text-[11px] text-text-muted mt-1 font-mono">
            {distKm.toFixed(0)} km vers La Mecque
          </div>
        )}

        {hasCompass && deltaToQibla != null && (
          <div className={`text-xs font-medium mt-2 ${tier.color}`}>
            {tier.label}
          </div>
        )}

        {!hasCompass && (
          <div className="text-[11px] text-text-muted mt-2">
            Tenez l'appareil à plat
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export default MiniQiblaCompass;
